import { useState, useEffect } from "react";
import logoPrimary from "./assets/logo-primary.png";

const links = [
  { label: "Início", href: "#hero" },
  { label: "Serviços", href: "#servicos" },
  { label: "Como fazemos", href: "#processo" },
  { label: "História", href: "#historia" },
  { label: "Portfólio", href: "#portfolio" },
  { label: "Stack", href: "#stack" },
  { label: "Time", href: "#time" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const NAV_OFFSET = 140;
    const firstId = links[0].href.replace("#", "");

    const update = () => {
      setScrolled(window.scrollY > 20);

      if (window.scrollY < 80) {
        setActive(firstId);
        return;
      }

      let current = firstId;
      for (const { href } of links) {
        const id = href.replace("#", "");
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= NAV_OFFSET) current = id;
      }
      setActive(current);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const handleLinkClick = (id) => {
    if (id) setActive(id);
    setMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "border-b border-night-700/80 bg-night-900/90 shadow-lg shadow-black/30 backdrop-blur-md"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
          <a href="#hero" onClick={() => handleLinkClick("hero")}>
            <img
              src={logoPrimary}
              alt="Caiena e Estumano Developers"
              className="h-11 w-auto object-contain sm:h-11 md:h-12"
            />
          </a>

          <nav className="hidden items-center gap-1 md:flex">
            {links.map((link) => {
              const id = link.href.replace("#", "");
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => handleLinkClick(id)}
                  className={`group/nav relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    active === id
                      ? "text-brand-100"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute inset-0 -z-10 rounded-full border transition-all duration-200 ${
                      active === id
                        ? "border-brand-400/40 bg-brand-500/20 opacity-100 shadow-[0_0_18px_-4px_rgba(111,60,255,0.5)]"
                        : "border-transparent bg-brand-500/0 opacity-0 group-hover/nav:border-brand-400/30 group-hover/nav:bg-brand-500/10 group-hover/nav:opacity-100 group-hover/nav:shadow-[0_0_14px_-4px_rgba(111,60,255,0.35)]"
                    }`}
                  />
                  {link.label}
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute -bottom-0.5 left-1/2 h-[2px] -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-400 to-accent-400 transition-all duration-300 ${
                      active === id
                        ? "w-6 opacity-100"
                        : "w-0 opacity-0 group-hover/nav:w-4 group-hover/nav:opacity-80"
                    }`}
                  />
                </a>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="https://wa.me/559183423638"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden rounded-full bg-gradient-to-r from-brand-500 via-brand-400 to-accent-500 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-brand-700/30 transition hover:brightness-110 md:inline-flex"
            >
              Falar com especialistas
            </a>

            <button
              aria-label="Abrir menu"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-9 w-9 flex-col items-center justify-center gap-[5px] rounded-lg border border-night-700 bg-night-800/70 transition hover:border-brand-400/50 md:hidden"
            >
              <span
                className={`h-[2px] w-5 rounded bg-slate-200 transition-all duration-300 ${
                  menuOpen ? "translate-y-[7px] rotate-45" : ""
                }`}
              />
              <span
                className={`h-[2px] w-5 rounded bg-slate-200 transition-all duration-300 ${
                  menuOpen ? "opacity-0" : ""
                }`}
              />
              <span
                className={`h-[2px] w-5 rounded bg-slate-200 transition-all duration-300 ${
                  menuOpen ? "-translate-y-[7px] -rotate-45" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          menuOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setMenuOpen(false)}
      />

      <div
        className={`fixed right-0 top-0 z-50 flex h-full w-72 flex-col border-l border-night-700 bg-night-900 p-6 shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="mb-8 flex items-center justify-between gap-3">
          <img src={logoPrimary} alt="C&E Developers" className="h-12 w-auto max-w-[min(100%,240px)] object-contain" />
          <button
            aria-label="Fechar menu"
            onClick={() => setMenuOpen(false)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-night-700 text-slate-400 transition hover:border-brand-400/50 hover:text-white"
          >
            ✕
          </button>
        </div>

        <nav className="flex flex-col gap-1">
          {links.map((link) => {
            const id = link.href.replace("#", "");
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={() => handleLinkClick(id)}
                className={`rounded-xl px-4 py-3 text-sm font-medium transition ${
                  active === id
                    ? "border border-brand-500/40 bg-brand-500/15 text-brand-100"
                    : "text-slate-300 hover:bg-night-800 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            );
          })}
        </nav>

        <div className="mt-auto border-t border-night-700 pt-6">
          <a
            href="https://wa.me/559183423638"
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center rounded-full bg-gradient-to-r from-brand-500 via-brand-400 to-accent-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-brand-700/30 transition hover:brightness-110"
          >
            Falar com especialistas
          </a>
          <p className="mt-4 text-center text-xs text-slate-500">
            Aceitando novos projetos em 2026
          </p>
        </div>
      </div>
    </>
  );
}
